/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {CustomMouseEvent, Events} from './Events';
import {ExternalConfiguration} from './ExternalConfiguration';
import {LocationLike, ObjectWithHref, ObjectWithPath} from '@adobe/exc-app/page';
import Messenger from './Messenger';

function splitPath(path: string, separator: string): string {
  const startsWithSlash = path.startsWith('/');
  const idx = path.indexOf(separator);
  const res = idx > -1 ? path.slice(idx + separator.length) : path;
  return res.startsWith('/') || !startsWithSlash ? res : `/${res}`;
}

/**
 * Reset a link or anchor URL that has been modified by the
 * context menu.
 * @param target The link or anchor targeted by context menu event.
 * @param href The original url of the target link or anchor.
 */
function setupUrlReset(target: HTMLElement, href: string): void {
  const events = ['click', 'keydown', 'mouseleave'];
  function setHref(event: Event): void {
    const t = event.target as HTMLAnchorElement;
    t.href = href;
    events.forEach(key => t.removeEventListener(key, setHref));
  }
  events.forEach(key => target.addEventListener(key, setHref));
}

/**
 * Finds and validates the true anchor element of a mouse event. Returns
 * undefined if the anchor is meant to be ignored (data-unified-shell-ignore).
 * @param {CustomMouseEvent} event Mouse event to process
 * @returns {HTMLAnchorElement=} The target anchor or undefined if
 *   data-unified-shell-ignore is true
 */
function getTarget(event: CustomMouseEvent): HTMLAnchorElement | undefined {
  let target = event.target as HTMLAnchorElement;
  target = target.closest('a') || target;
  return target.getAttribute('data-unified-shell-ignore') !== 'true' ? target : undefined;
}

/**
 * Called when receiving a message from the parent frame.
 * @param config The current external configuration.
 * @param location A ShellUrlObject to describe the location lookup.
 * @returns A valid full shell URL (as a string).
 */
export function generateShellUrl(config: ExternalConfiguration, location: LocationLike, newApp?: boolean): string {
  let locationHref = (location as ObjectWithHref).href || '';
  // If the href is already a shell URL, there is nothing more to do.
  if (/^https:\/\/experience(-qa|-stage)?.adobe.com/.test(locationHref)) {
    return locationHref;
  }

  const {baseFrameUrl, discovery, externalQueryParams, historyType} = config;
  const baseUrl = new URL(config.baseUrl);
  const isHashHistory = historyType === 'HASH';
  const configUrl = new URL(baseFrameUrl);
  const shellUrl = new URL(baseUrl.origin);
  const {path, sandbox} = location as ObjectWithPath;

  if (!locationHref) {
    // Solutions URLs are "mounted" at the URL provided by the solution config
    // or by the discovery endpoint. The `baseFrameUrl` is the root URL that is
    // mounted at the `/@tenant/solution` path. If `baseFrameUrl` came from a
    // discovery call, it will be a fully formed path to a page with their
    // application. We can't just append the new path to that URL and instead
    // must treat the path as an absolute path (e.g. completely replacing the
    // existing path). In the non-discovery case, the `baseFrameUrl` is a
    // non-fully formed path within the application (e.g. /solutions/ddam). Any
    // new path should be appended to the `baseFrameUrl`'s path.
    const base = discovery ? configUrl.origin : baseFrameUrl;
    let locationPath = path || '';

    // Instead of updating the current shellUrl, newApp generates a shellUrl
    // for use in opening a window or tab for another solution with the provided
    // path while maintaining the query parameters present in the current url.
    if (newApp) {
      const tenant = `/@${config.tenant}`;
      shellUrl.hash = sandbox ? `${tenant}/sname:${sandbox}${locationPath}` : `${tenant}${locationPath}`;
      shellUrl.search = externalQueryParams;
      return shellUrl.toString();
    }
    locationPath = isHashHistory && locationPath.indexOf('#') === -1 ? `/#${locationPath}` : locationPath || '/';
    locationHref = `${base}${locationPath}`;
  }
  const url = new URL(locationHref);

  let pathname = isHashHistory ? url.hash.replace(/^#\//, '/') : url.pathname;
  let hash = baseUrl.hash;
  // Split baseUrl.hash by tenant and solution path to insert sandbox.
  if (sandbox) {
    const matches = baseUrl.hash.match(`^#(/@[^/]*)?(/sname:[^/]+)?(/[^/]+)`);
    if (matches) {
      const tenant = matches[1];
      // matches[2] will be the sandbox if it exists in the baseUrl
      const solutionPath = matches[3];
      hash = `#${tenant}/sname:${sandbox}${solutionPath}`;
    }
  }
  pathname = `${hash}${discovery ? pathname : splitPath(pathname, configUrl.pathname)}`;

  // Need to preserve the search and hash values on the URL being loaded.
  shellUrl.hash = `${pathname}${url.search}${isHashHistory ? '' : url.hash}`;
  shellUrl.search = externalQueryParams;

  return shellUrl.toString();
}

/**
 * Handles keydown events (escape in particular) to send focus back up to the parent.
 * @param event A keyboard event.
 * @param messenger A messenger instance on which to proxy events.
 */
export function onKeyDown(event: KeyboardEvent, messenger: Messenger): void {
  // If user presses escape key, simulate click on iframe
  if (event.keyCode === 27) {
    messenger.send(Events.FOCUS, {});
  }
}

/**
 * Handles the context menu event to ensure "Open in New Tab/Window" is correct.
 * @param event The mouse event from the contextmenu.
 * @param config The external configuration.
 */
export function onContextMenu(event: MouseEvent, config: ExternalConfiguration, allowedHosts: string[]): void {
  let target = getTarget(event);
  if (!target) {
    return;
  }

  if (allowedHosts.includes(target.host)) {
    const originalHref = target.href;
    setupUrlReset(target, originalHref);
    target.href = generateShellUrl(config, {href: originalHref});
  }
}

/**
 * Handle command click and regular click.
 * @param event The click event being handled.
 */
export function onClick(event: CustomMouseEvent, config: ExternalConfiguration, allowedHosts: string[], messenger: Messenger): void {
  // Event came from the Shell via user input. Don't let this propogate back
  // to the Shell.
  if (event.fromShell) {
    return;
  }

  let target = getTarget(event);
  if (!target) {
    return;
  }
  const targetAttribute = target.getAttribute('target') || '';
  let browserUrl = new URL(config.baseUrl);

  // Hack for Target's footer
  if (config.appId === 'target' && target.nodeName === 'A' && (event.target as HTMLAnchorElement).closest('.endor-Footer')) {
    event.preventDefault();
    window.open(target.href);
    messenger.send(Events.CLICK, {});
    return;
  }

  // Handle Unified Shell links to avoid a full page refresh
  if (target.host === browserUrl.host && ['_parent', '_top'].indexOf(targetAttribute) > -1) {
    let targetUrl = new URL(target.href);
    // if the path isn't '/', we need to construct the url to navigate to
    if (targetUrl.pathname.length > 1) {
      browserUrl.hash = `#${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
      target.setAttribute('href', browserUrl.toString());
    }
    // Otherwise, ensure that the query params are maintained
    if (targetUrl.pathname === '/' && browserUrl.search) {
      targetUrl.search = browserUrl.search;
      target.setAttribute('href', targetUrl.toString());
    }
  }

  // Window location and link location are the same, so we need to make sure
  // it's wrapped in a Shell.
  if (allowedHosts.includes(target.host)) {
    // The new tab key is different based on platform (OSX uses command while
    // windows uses control)
    const newTabKey = /[Mm]ac/.test(window.navigator.platform) ? event.metaKey : event.ctrlKey;
    // Either the new tab key was used or there is a target="_blank" attribute
    // on the anchor. We need to open this in a new window within the Shell.
    // Block the default action since we don't want to open the
    // link in a new window without the Shell.
    if (newTabKey || target.getAttribute('target') === '_blank') {
      event.preventDefault();
      window.open(generateShellUrl(config, {href: target.href}));
      messenger.send(Events.CLICK, {});
    }
    return;
  }

  // Check if href already has target attribute so we overwrite _blank
  if (target.href && !target.hasAttribute('target')) {
    // open the link in the parent window
    target.setAttribute('target', '_parent');
  }
  messenger.send(Events.CLICK, {});
}

/**
 * Handle middle mouse down button.
 */
export function onMouseDown(event: CustomMouseEvent, config: ExternalConfiguration, allowedHosts: string[]): void {
  let target = getTarget(event);
  if (!target) {
    return;
  }
  // We only care about middle mouse button events here. onClick handles others.
  if (event.button !== 1 || !allowedHosts.includes(target.host)) {
    return;
  }
  window.open(generateShellUrl(config, {href: target.href}));
}
